import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import axios from "axios";
import { toast } from "react-toastify";
import { FiArrowLeft } from "react-icons/fi";

const EditRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [defaultFileName, setDefaultFileName] = useState("");
  const [loading, setLoading] = useState(true);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const changesExist =
      formData.request_name !== originalFormData.request_name ||
      formData.description !== originalFormData.description ||
      formData.category_name !== originalFormData.category_name ||
      (formData.request_file && formData.request_file.name !== defaultFileName);

    setHasChanges(changesExist);
  }, [formData, originalFormData, defaultFileName]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const meRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          { withCredentials: true }
        );

        if (!meRes.data.success) {
          toast.error("Session expired. Please log in.");
          return navigate("/user/login");
        }

        const userId = meRes.data.id;

        const requestRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/request/byId/${id}`,
          { credentials: "include" }
        );
        const requestData = await requestRes.json();

        if (!requestRes.ok) {
          throw new Error(requestData.message || "Failed to fetch request");
        }

        if (requestData.user_id !== userId) {
          throw new Error("You are not authorized to edit this request");
        }

        const initialFormData = {
          request_name: requestData.request_name,
          description: requestData.description,
          category_name: requestData.category_name,
        };

        setFormData(initialFormData);
        setOriginalFormData(initialFormData);
        setDefaultFileName(requestData.request_file);
      } catch (error) {
        toast.error(error.message);
        navigate("/user/requests");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, request_file: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("request_name", formData.request_name);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("category_name", formData.category_name);
      if (formData.request_file) {
        formDataToSend.append("request_file", formData.request_file);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/request/${id}`,
        {
          method: "PUT",
          body: formDataToSend,
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg);
      }

      toast.success("Request updated successfully!");

      setOriginalFormData({
        request_name: formData.request_name,
        description: formData.description,
        category_name: formData.category_name,
      });

      setFormData((prev) => ({ ...prev, request_file: null }));
      setDefaultFileName(formData.request_file?.name || defaultFileName);

      setTimeout(() => {
        navigate("/user/requests");
      }, 1500);
    } catch (error) {
      toast.error(`Update failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ClipLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <h5
        onClick={() => navigate(-1)}
        className="text-blue-600 p-1 cursor-pointer rounded-lg"
      >
        <FiArrowLeft /> back
      </h5>
      <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Update Request
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Request Name *
              </label>
              <input
                type="text"
                name="request_name"
                value={formData.request_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <input
                type="text"
                name="category_name"
                value={formData.category_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Brief description of the request..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Request File *
            </label>
            <input
              type="file"
              name="request_file"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {formData.request_file ? (
              <p className="text-gray-500 text-sm mt-1">
                {formData.request_file.name}
              </p>
            ) : (
              defaultFileName && (
                <p className="text-gray-500 text-sm mt-1">
                  Existing File: {defaultFileName}
                </p>
              )
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!hasChanges || isSubmitting}
              className={`px-6 py-2 rounded-lg text-white font-medium transition-colors flex items-center justify-center min-w-32 ${
                isSubmitting || !hasChanges
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRequest;
